import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Kbd, Label, Modal, SearchField } from '@heroui/react';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    paletteRowElementId,
    type ActionArea,
    type ControlAction,
    type PaletteResult,
} from '../../controlCenterMeta';

const LIST_ID = 'control-center-palette-list';

type CommandPaletteViewProps = {
    isOpen: boolean;
    onClose: () => void;
    query: string;
    onQueryChange: (next: string) => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
    results: PaletteResult[];
    selectable: ControlAction[];
    highlight: number;
    highlightedId: string | null;
    onHighlight: (index: number) => void;
    onRun: (action: ControlAction) => void;
    totalCount: number;
};

type PaletteRowProps = {
    action: ControlAction;
    label: string;
    isHighlighted: boolean;
    onHighlight: () => void;
    onRun: (action: ControlAction) => void;
};

type MutedPaletteRowProps = {
    /** The action's id — the row's `aria-activedescendant` target. */
    rowId: string;
    icon: string;
    area: ActionArea;
    label: string;
};

type HintProps = {
    keys: React.ReactNode;
    label: string;
};

/**
 * The command palette — the page's fastest route, and the reason the catalog
 * below can stay quiet.
 *
 * **Refused matches still appear**, muted and unselectable. A search that
 * silently omits a row this session may not run reads as "there is no such
 * thing", which is a worse answer than "not for you".
 *
 * The only metadata a row carries is its area, plus a live value where the
 * action already reads one. Everything else — availability, where it opens —
 * is either a glyph or absent, because a palette row is read in the second it
 * takes to press Enter.
 */
export const CommandPaletteView: React.FC<CommandPaletteViewProps> = ({
    isOpen,
    onClose,
    query,
    onQueryChange,
    onKeyDown,
    results,
    selectable,
    highlight,
    highlightedId,
    onHighlight,
    onRun,
    totalCount,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Modal.Backdrop>
                <Modal.Container size="md">
                    <Modal.Dialog
                        className="w-full max-w-xl overflow-hidden p-0"
                        {...getNodeTestIdProp('control-center-palette')}
                    >
                        <div className="border-b border-separator px-4 py-2.5">
                            <SearchField
                                aria-controls={LIST_ID}
                                autoFocus
                                fullWidth
                                onChange={onQueryChange}
                                onKeyDown={onKeyDown}
                                value={query}
                                variant="secondary"
                                {...getNodeTestIdProp(
                                    'control-center-palette-search',
                                )}
                            >
                                <Label className="sr-only">
                                    {t('controlCenter.palette.searchLabel')}
                                </Label>
                                <SearchField.Group>
                                    <SearchField.SearchIcon />
                                    <SearchField.Input
                                        aria-activedescendant={
                                            highlightedId
                                                ? paletteRowElementId(
                                                      highlightedId,
                                                  )
                                                : undefined
                                        }
                                        placeholder={t(
                                            'controlCenter.palette.placeholder',
                                        )}
                                    />
                                    <SearchField.ClearButton />
                                </SearchField.Group>
                            </SearchField>
                        </div>

                        <div className="flex items-center justify-between px-4 pt-3">
                            <span className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted">
                                {t('controlCenter.palette.groupActions')}
                            </span>
                            <span className="text-[11.5px] text-muted tabular-nums">
                                {t('controlCenter.palette.results', {
                                    count: results.length,
                                })}
                            </span>
                        </div>

                        <ul
                            aria-label={t('controlCenter.palette.searchLabel')}
                            className="flex max-h-[min(60vh,380px)] flex-col overflow-y-auto p-2"
                            id={LIST_ID}
                            role="listbox"
                            {...getNodeTestIdProp(
                                'control-center-palette-rows',
                            )}
                        >
                            {results.map((result) =>
                                result.kind === 'available' ? (
                                    <PaletteRow
                                        action={result.action}
                                        isHighlighted={
                                            selectable[highlight]?.id ===
                                            result.action.id
                                        }
                                        key={result.action.id}
                                        label={result.label}
                                        onHighlight={() =>
                                            onHighlight(
                                                selectable.findIndex(
                                                    (candidate) =>
                                                        candidate.id ===
                                                        result.action.id,
                                                ),
                                            )
                                        }
                                        onRun={onRun}
                                    />
                                ) : (
                                    <MutedPaletteRow
                                        area={result.action.area}
                                        icon={result.action.icon}
                                        key={result.action.id}
                                        label={result.label}
                                        rowId={result.action.id}
                                    />
                                ),
                            )}

                            {query.trim().length === 0 ? (
                                <li
                                    className="px-2.5 py-6 text-center text-sm text-muted"
                                    {...getNodeTestIdProp(
                                        'control-center-palette-idle',
                                    )}
                                >
                                    {t('controlCenter.palette.idle', {
                                        count: totalCount,
                                    })}
                                </li>
                            ) : null}

                            {query.trim().length > 0 && results.length === 0 ? (
                                <li
                                    className="px-2.5 py-6 text-center text-sm text-muted"
                                    {...getNodeTestIdProp(
                                        'control-center-palette-no-match',
                                    )}
                                >
                                    {t('controlCenter.palette.noMatch', {
                                        query,
                                    })}
                                </li>
                            ) : null}
                        </ul>

                        <div className="flex flex-wrap items-center gap-3.5 border-t border-separator bg-surface-secondary/60 px-4 py-2.5">
                            <Hint
                                keys={
                                    <Kbd>
                                        <Kbd.Abbr keyValue="up" />
                                        <Kbd.Abbr keyValue="down" />
                                    </Kbd>
                                }
                                label={t(
                                    'controlCenter.palette.hints.navigate',
                                )}
                            />
                            <Hint
                                keys={
                                    <Kbd>
                                        <Kbd.Content>↵</Kbd.Content>
                                    </Kbd>
                                }
                                label={t('controlCenter.palette.hints.run')}
                            />
                            <Hint
                                keys={
                                    <Kbd>
                                        <Kbd.Abbr keyValue="shift" />
                                        <Kbd.Content>P</Kbd.Content>
                                    </Kbd>
                                }
                                label={t('controlCenter.palette.hints.pin')}
                            />
                            <div className="flex-1" />
                            <span className="text-[11px] text-muted">
                                {t('controlCenter.palette.footnote')}
                            </span>
                        </div>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};

/**
 * One runnable row.
 *
 * Hovering moves the highlight so the mouse and the keyboard agree about which
 * row Enter would take — two "current" rows on one list is how somebody runs
 * the action they were not looking at.
 */
const PaletteRow: React.FC<PaletteRowProps> = ({
    action,
    label,
    isHighlighted,
    onHighlight,
    onRun,
}) => {
    const { t } = useTranslation('pages');
    const area = t(`controlCenter.areas.${action.area}`);

    return (
        <li
            aria-selected={isHighlighted}
            className={cn(
                'flex h-11 cursor-[var(--cursor-interactive)] items-center gap-3 rounded-[10px] px-2.5',
                isHighlighted && 'bg-accent/12',
            )}
            id={paletteRowElementId(action.id)}
            onClick={() => onRun(action)}
            onMouseMove={onHighlight}
            role="option"
            {...getNodeTestIdProp(`control-center-palette-option-${action.id}`)}
        >
            <Icon
                aria-hidden
                className={cn(
                    'size-4 shrink-0',
                    isHighlighted ? 'text-accent' : 'text-muted',
                )}
                icon={action.icon}
            />
            <span className="flex min-w-0 flex-1 flex-col">
                <span
                    className={cn(
                        'truncate text-[13.5px] text-foreground',
                        isHighlighted && 'font-medium',
                    )}
                >
                    {label}
                </span>
                <span className="truncate text-[11px] text-muted">{area}</span>
            </span>
            {action.opensOwnPage ? (
                <Icon
                    aria-hidden
                    className="size-3.5 shrink-0 text-muted"
                    icon="solar:arrow-right-up-linear"
                />
            ) : null}
            {isHighlighted ? (
                <span aria-hidden className="shrink-0">
                    <Kbd>
                        <Kbd.Content>↵</Kbd.Content>
                    </Kbd>
                </span>
            ) : null}
        </li>
    );
};

/**
 * A match this session may not run.
 *
 * Rendered rather than filtered out, so the search never dead-ends silently: a
 * query for "employee" that returned nothing would read as "there is no such
 * thing", which is a worse answer than "not you". It is `aria-disabled` and
 * unreachable by Enter — `selectableResults` only ever yields `available` rows.
 *
 * It has **no popover**, unlike a refused catalog row. These are
 * `role="option"` inside a listbox, and running one closes the list the row
 * lived in — there would be nothing left to anchor an arrow to.
 *
 * Until BOS-181 this row had a second kind: a coming-soon match from a roadmap
 * list that no longer exists.
 */
const MutedPaletteRow: React.FC<MutedPaletteRowProps> = ({
    rowId,
    icon,
    area,
    label,
}) => {
    const { t } = useTranslation('pages');

    return (
        <li
            aria-disabled
            className="flex h-11 items-center gap-3 rounded-[10px] px-2.5 opacity-60"
            id={paletteRowElementId(rowId)}
            role="option"
            {...getNodeTestIdProp(`control-center-palette-refused-${rowId}`)}
        >
            <Icon
                aria-hidden
                className="size-4 shrink-0 text-muted"
                icon={icon}
            />
            <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-[13.5px] text-muted">
                    {label}
                </span>
                <span className="truncate text-[11px] text-muted">
                    {t('controlCenter.palette.areaRefused', {
                        area: t(`controlCenter.areas.${area}`),
                    })}
                </span>
            </span>
        </li>
    );
};

const Hint: React.FC<HintProps> = ({ keys, label }) => (
    <span className="flex items-center gap-1.5 text-[11px] text-muted">
        {keys}
        {label}
    </span>
);
